import React from 'react';

import {Flex, Space, Tag, Tooltip} from 'antd';

import styles from './index.less';
import classNames from 'classnames';
import {useDispatch, useModel} from 'umi';
import {hasAccess} from '@/utils/access';
import {BranchModel, ServiceModel} from '@/typings/models/Location';
import {LeadModel} from '@/typings/models/Lead';
import PERMISSIONS from '@/constants/permissions';
import {AssigneeModel} from '@/typings/models/Assignee';
import LeadName from '@/components/Lead/LeadName';
import {TeamOutlined} from '@ant-design/icons';
import AgentName from '@/components/Agent/AgentName';
import {setColorOpacity} from '@/utils/functions';
import isEmpty from 'lodash/isEmpty';
import OpenHoursOverview from '@/components/OpenHours/OpenHoursOverview';

export interface IActionTagProps {
  children: React.ReactNode;
  onClick: (e: MouseEvent) => void;
  className?: string;
  tooltip?: any;
  size?: string;
  access?: string[];
  clickable?: boolean;
  color?: string;
  style?: React.CSSProperties;
  [restProp: string]: any;
}

export default function ActionTag({
                                    clickable = true,
                                    children,
                                    size,
                                    tooltip,
                                    onClick,
                                    className,
                                    access,
                                    ...props
                                  }: IActionTagProps) {
  const { initialState } = useModel('@@initialState');

  const handleClick = (e) => {
    if (!clickable) return;
    e.stopPropagation();
    onClick(e);
  };
  if (Array.isArray(access) && clickable)
    clickable = hasAccess(initialState.currentUser, access);
  if (clickable)
    props.onClick = handleClick;

  const content = <Tag color={'default'} className={classNames(styles.actionTag, {
    [styles.large]: size === 'large',
    [styles.medium]: size === 'medium',
    [styles.readonly]: !clickable,
  }, className)} {...props}>
    {children}
  </Tag>;

  return tooltip ? <Tooltip title={tooltip} placement={'topLeft'}>{content}</Tooltip> : content;
}


export const BranchActionTag = ({ showAddressTooltip, branch, onClick, ...props }: {
  branch: BranchModel,
  showAddressTooltip?: boolean,
  onClick?: () => void,
  [restProp: string]: any
}) => {
  const dispatch = useDispatch();
  const onBranchClick = (e) => {
    e.stopPropagation();
    if (onClick) onClick(e);
    dispatch({
      type: 'location/getBranch',
      payload: { branchId: branch.id },
    });
    dispatch({
      type: 'location/toggleBranchDrawer',
      payload: {
        branch,
      },
    });
  };
  if (!branch) return null;
  const content = <ActionTag access={[PERMISSIONS.Branches.EDIT, PERMISSIONS.BUSINESS_PARTNER]} onClick={onBranchClick}
                             size={'large'} {...props}>
    {branch?.name}
  </ActionTag>;

  if (showAddressTooltip) {
    return <Tooltip title={branch?.address} placement={'topLeft'}>
      {content}
    </Tooltip>;
  }
  return content;
};


export const BranchServiceActionTag = ({ branch, service, onClick, size = 'medium', ...tagProps }: {
  branch: BranchModel,
  service: ServiceModel,
  size?: string,
  onClick?: () => void
}) => {
  const dispatch = useDispatch();
  const onBranchServiceClick = () => {
    if (onClick) onClick();
    dispatch({
      type: 'location/getBranch',
      payload: { branchId: branch.id },
    });
    dispatch({
      type: 'location/toggleBranchDrawer',
      payload: {
        branch,
        service,
      },
    });
  };


  const openingHours = service?.calendar?.Availability.filter(a => a?.allow_day_and_time).map(a => a?.allow_day_and_time) || [];

  return (
    <ActionTag
      access={[PERMISSIONS.BranchServices.EDIT, PERMISSIONS.BUSINESS_PARTNER]}
      onClick={onBranchServiceClick}
      size={size}
      {...tagProps}
    >
      <Flex style={{ width: '100%', paddingLeft: 8 }} gap={4} justify={'space-between'} align={'center'}>
        <Flex style={{ flex: 1, minWidth: 0 }} gap={8} align={'center'}>
          {service.service_type?.icon}
          <span className={styles.serviceName}>{service.service_type?.name || service?.name}</span>
        </Flex>
        <Flex gap={2} align={'center'} style={{ flexShrink: 0 }}>
          <Tag
            bordered={false}
            style={{ marginRight: 0 }}
            color={service?.staff_count > 0 ? 'default' : 'red'}
          >
            <Space size={2}>
              <TeamOutlined />
              <span>{service?.staff_count}</span>
            </Space>
          </Tag>
          <OpenHoursOverview openingHours={openingHours} />
        </Flex>
      </Flex>
    </ActionTag>
  );
};

export const AgentActionTag = ({
                                 agent,
                                 showAgentColor = false,
                                 hideInactiveFlag,
                                 onClick,
                                 clickable = true,
                                 hideAvatar = false,
                                 justAvatar = false,
                                 showNameInTooltip = false,
                                 children,
                                 ...props
                               }: {
  agent: AssigneeModel,
  clickable?: boolean,
  hideInactiveFlag?: boolean,
  showAgentColor?: boolean,
  hideAvatar?: boolean,
  justAvatar?: boolean,
  showNameInTooltip?: boolean,
  onClick?: () => void,
  className?: string,
  color?: string,
  style?: React.CSSProperties,
  children?: React.ReactNode,
  [restProp: string]: any,
}) => {
  const dispatch = useDispatch()
  const onAgentClick = (e) => {
    e.stopPropagation();
    if (!clickable) return;
    if (onClick) onClick(e);
    dispatch({
      type: 'user/toggleDetailsDrawer',
      payload: {
        agentId: agent.id,
        agent
      }
    })
  };

  if (isEmpty(agent)) return null

  const withAgentColor = showAgentColor && !isEmpty(agent?.color);

  return (
    <>
      <ActionTag
        access={[PERMISSIONS.Users.EDIT, PERMISSIONS.Users.AVAILABILITY_EDIT, PERMISSIONS.Users.PRIORITY_EDIT, PERMISSIONS.BUSINESS_PARTNER]}
        onClick={onAgentClick}
        clickable={clickable}
        size={'large'}
        {...props}
        className={classNames(props.className, { [styles.agentColorTag]: withAgentColor })}
        color={withAgentColor ? undefined : props.color}
        style={withAgentColor ? {
          '--agent-tag-bg': setColorOpacity(agent.color, 15),
          '--agent-tag-border': setColorOpacity(agent.color, 60),
        } as React.CSSProperties : props.style}
      >
        {children || <AgentName showNameInTooltip={showNameInTooltip} hideAvatar={hideAvatar} justAvatar={justAvatar} hideInactiveFlag={hideInactiveFlag} agent={agent} />}
      </ActionTag>
    </>
  );
};

export const LeadActionTag = ({ lead, highlightedAppointmentId, onClick, size = 'middle' }: {
  lead: LeadModel,
  highlightedAppointmentId?: string,
  onClick?: (e: MouseEvent) => void,
  size?: string,
  activeTab?: string,
}) => {
  const dispatch = useDispatch();

  const onLeadClick = (e) => {
    e.stopPropagation();
    if (onClick) onClick(e);
    dispatch({
      type: 'leads/toggleLeadDetailsDrawer',
      payload: {
        lead,
        highlightedAppointmentId
      },
    });
  };
  return <ActionTag
    className={'lead-action-tag'}
    onClick={onLeadClick}
    size={size}
    onMouseOver={e => e.stopPropagation()}>
    <LeadName lead={lead} readonly />
  </ActionTag>;
};
